import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';

import { SCUILoadingLinear, SCUIDateRangerV2, SCUINoRecordComponent } from '../../../atomic/atoms';
import SCUIDownloadButton from '../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';

import { StudioShotReportQuery } from '../../../services/studio/StudioQuery/StudioShotReport/StudioShotReportQuery.services';
import { isValidBarcode } from '../../helper/studio_helper';
import { downloadExcel } from '../../../utils/excelFileHelper';

const CONTROL_HEIGHT = 48;

type SearchMode = 'input' | 'date' | 'unaccepted';

type StudioShotReportRow = {
    shotHistoryId: number | null;
    productId: number;
    specialCode: string;
    colorCode: string;
    colorName: string;
    barcode: number;
    sampleDeliveryDate: string | null;
    acceptanceLocation: string;
    mannequin: string;
    size: string;
    line: string;
    seasonCode: string;
    productName: string;
    shotDate: string;
    shotStatus: string;
    merchSubGroup: string;
    processedByUser: string;
    createdAt?: string | null;
};

type AppliedFilters = {
    Barcode?: number;
    SpecialCode?: string;
    ColorCode?: string;
    AcceptanceDateStart?: string;
    AcceptanceDateEnd?: string;
    IsUnaccepted?: boolean;
};

const mapRow = (item: any): StudioShotReportRow => ({
    shotHistoryId: item.shotHistoryId !== undefined ? item.shotHistoryId : item.ShotHistoryId,
    productId: item.productId || item.ProductId || 0,
    specialCode: item.specialCode || item.SpecialCode || '',
    colorCode: item.colorCode || item.ColorCode || '',
    colorName: item.colorName || item.ColorName || '',
    barcode: item.barcode || item.Barcode,
    sampleDeliveryDate: item.sampleDeliveryDate || item.SampleDeliveryDate || null,
    acceptanceLocation: item.acceptanceLocation || item.AcceptanceLocation || '',
    mannequin: item.mannequin || item.Mannequin || '',
    size: item.size || item.Size || '',
    line: item.line || item.Line || '',
    seasonCode: item.seasonCode || item.SeasonCode || '',
    productName: item.productName || item.ProductName || '',
    shotDate: item.shotDate || item.ShotDate,
    shotStatus: item.shotStatus || item.ShotStatus || '',
    merchSubGroup: item.merchSubGroup || item.MerchSubGroup || '',
    processedByUser: item.processedByUser || item.ProcessedByUser || '',
    createdAt: item.createdAt || item.CreatedAt || null,
});

const toExcelRow = (row: StudioShotReportRow) => ({
    specialCode: row.specialCode,
    colorCode: row.colorCode,
    colorName: row.colorName,
    barcode: row.barcode,
    sampleDeliveryDate: row.sampleDeliveryDate || '',
    acceptanceLocation: row.acceptanceLocation,
    mannequin: row.mannequin,
    size: row.size,
    line: row.line,
    seasonCode: row.seasonCode,
    productName: row.productName,
    shotDate: row.shotDate || '',
    shotStatus: row.shotStatus,
    merchSubGroup: row.merchSubGroup,
    processedByUser: row.processedByUser,
});

const parseSpecialAndColor = (value: string): { specialCode: string; colorCode: string } | null => {
    const trimmed = value.trim();
    const dashIndex = trimmed.indexOf('-');

    // Case 1: If there is a dash, split by dash (e.g., "S6EQ84Z8-ZR3" or "ABC123-456")
    if (dashIndex > 0 && dashIndex < trimmed.length - 1) {
        const specialCode = trimmed.slice(0, dashIndex).trim();
        const colorCode = trimmed.slice(dashIndex + 1).trim();

        if (specialCode && colorCode) {
            return { specialCode, colorCode };
        }
    }

    // Case 2: If there is no dash, but it's a keycode (typically 11 characters: 8 chars special code + 3 chars color code, e.g., "S6EQ84Z8ZR3")
    if (dashIndex === -1 && trimmed.length === 11) {
        const specialCode = trimmed.slice(0, 8).trim();
        const colorCode = trimmed.slice(8).trim();

        if (specialCode && colorCode) {
            return { specialCode, colorCode };
        }
    }

    return null;
};

const buildFiltersFromInput = (inputType: string, searchText: string): AppliedFilters | null => {
    const trimmed = searchText.trim();

    if (inputType === '3') {
        if (!isValidBarcode(trimmed)) {
            return null;
        }
        return { Barcode: Number(trimmed) };
    }

    if (inputType === '2') {
        const parsed = parseSpecialAndColor(trimmed);
        if (!parsed) {
            return null;
        }
        return { SpecialCode: parsed.specialCode, ColorCode: parsed.colorCode };
    }

    if (inputType === '1') {
        if (!trimmed) {
            return null;
        }
        return { SpecialCode: trimmed };
    }

    return null;
};

const toAcceptanceDateRange = (startDate: string, endDate: string) => ({
    AcceptanceDateStart: startDate.includes('T') ? startDate : `${startDate}T00:00:00`,
    AcceptanceDateEnd: endDate.includes('T') ? endDate : `${endDate}T23:59:59`,
});

const buildAppliedFiltersFromSearch = (
    searchMode: SearchMode,
    inputType: string,
    searchText: string,
    startDate: string,
    endDate: string
): AppliedFilters | null => {
    if (searchMode === 'input') {
        return buildFiltersFromInput(inputType, searchText);
    }

    if (searchMode === 'unaccepted') {
        const filters: AppliedFilters = { IsUnaccepted: true };
        if (startDate && endDate) {
            Object.assign(filters, toAcceptanceDateRange(startDate, endDate));
        }
        return filters;
    }

    if (startDate && endDate) {
        return toAcceptanceDateRange(startDate, endDate);
    }

    return null;
};

export const StudioShotReport = () => {
    const { t } = useTranslation();

    const defaultStartDate = useMemo(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    }, []);

    const defaultEndDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().split('T')[0];
    }, []);

    const [searchMode, setSearchMode] = useState<SearchMode>('unaccepted');
    const [inputType, setInputType] = useState('3');
    const [searchText, setSearchText] = useState('');
    const [startDate, setStartDate] = useState<string>(defaultStartDate);
    const [endDate, setEndDate] = useState<string>(defaultEndDate);

    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(null);
    const [hasSearched, setHasSearched] = useState(false);

    const [rows, setRows] = useState<StudioShotReportRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);

    const selectListOptions = useMemo(
        () => [
            { value: '3', text: t('searchService.barcode') },
            { value: '2', text: t('searchService.specialAndColorCode') },
            { value: '1', text: t('searchService.specialCode') },
        ],
        [t]
    );

    const isSearchEnabled = useMemo(() => {
        if (searchMode === 'input') {
            return buildFiltersFromInput(inputType, searchText) !== null;
        }

        if (!startDate || !endDate) {
            return false;
        }

        return new Date(startDate) <= new Date(endDate);
    }, [searchMode, inputType, searchText, startDate, endDate]);

    const fetchData = useCallback(async () => {
        if (!appliedFilters) {
            return;
        }

        try {
            setLoading(true);
            const response = await StudioShotReportQuery.QueryStudioShotReport({
                ...appliedFilters,
                PageNumber: currentPage + 1,
                PageSize: pageSize,
            });

            if (response?.errors) {
                const errorMessages = Object.values(response.errors).flat().join('\n');
                Swal.fire(errorMessages, '', 'error');
                setRows([]);
                setTotalCount(0);
                return;
            }

            const rawItems = response?.items || response?.Items || [];
            const total = response?.totalCount ?? response?.TotalCount ?? 0;

            setRows(rawItems.map(mapRow));
            setTotalCount(total);
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.studioShotReport.fetchError'),
            });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [appliedFilters, currentPage, pageSize, t]);

    useEffect(() => {
        if (hasSearched && appliedFilters) {
            fetchData();
        }
    }, [hasSearched, appliedFilters, currentPage, pageSize, fetchData]);

    const handleSearchModeChange = (mode: SearchMode) => {
        setSearchMode(mode);
        setSearchText('');
        if (mode === 'unaccepted') {
            setStartDate(defaultStartDate);
            setEndDate(defaultEndDate);

            const startStr = `${defaultStartDate}T00:00:00`;
            const endStr = `${defaultEndDate}T23:59:59`;
            setCurrentPage(0);
            setHasSearched(true);
            setAppliedFilters({
                IsUnaccepted: true,
                AcceptanceDateStart: startStr,
                AcceptanceDateEnd: endStr,
            });
        } else {
            setStartDate('');
            setEndDate('');
            setHasSearched(false);
            setAppliedFilters(null);
            setRows([]);
            setTotalCount(0);
        }
    };

    const handleSearch = useCallback(() => {
        const filters = buildAppliedFiltersFromSearch(searchMode, inputType, searchText, startDate, endDate);
        if (!filters) {
            return;
        }

        setCurrentPage(0);
        setHasSearched(true);
        setAppliedFilters(filters);
    }, [searchMode, inputType, searchText, startDate, endDate]);

    useEffect(() => {
        // Initial search on mount for unaccepted products
        const startStr = `${defaultStartDate}T00:00:00`;
        const endStr = `${defaultEndDate}T23:59:59`;
        setCurrentPage(0);
        setHasSearched(true);
        setAppliedFilters({
            IsUnaccepted: true,
            AcceptanceDateStart: startStr,
            AcceptanceDateEnd: endStr,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExcelExport = async () => {
        if (!appliedFilters) {
            return [];
        }

        const exportPageSize = totalCount > 0 ? totalCount : 10000;
        const response = await StudioShotReportQuery.QueryStudioShotReport({
            ...appliedFilters,
            PageNumber: 1,
            PageSize: exportPageSize,
        });

        const rawItems = response?.items || response?.Items || [];
        const excelRows = rawItems.map(mapRow).map(toExcelRow);

        if (!excelRows.length) {
            Swal.fire({
                icon: 'info',
                title: t('general.noData'),
                showConfirmButton: false,
                timer: 1500,
            });
            return [];
        }

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'StudioShotReport',
            fileNameOverride: 'studio-shot-report',
        });

        return [];
    };

    const handleChangeDate = (event: any) => {
        setStartDate(event[0] || '');
        setEndDate(event[1] || '');
    };

    const columns = useMemo(
        () => [
            { field: 'specialCode', headerName: t('searchService.specialCode'), width: 130 },
            { field: 'colorCode', headerName: t('studio.colorCode'), width: 100 },
            { field: 'colorName', headerName: t('studio.colorName'), width: 130 },
            { field: 'barcode', headerName: t('studio.barcode'), width: 150 },
            {
                field: 'sampleDeliveryDate',
                headerName: t('studio.studioShotReport.sampleDeliveryDate'),
                width: 170,
            },
            {
                field: 'acceptanceLocation',
                headerName: t('studio.studioShotReport.acceptanceLocation'),
                width: 150,
            },
            { field: 'line', headerName: t('studio.itemCancellationGrid.line'), width: 100 },
            { field: 'productName', headerName: t('studio.productName'), flex: 1, minWidth: 160 },
            { field: 'merchSubGroup', headerName: t('studio.studioShotReport.merchSubGroup'), width: 150 },
            {
                field: 'shotDate',
                headerName: t('studio.studioShotReport.shotDate'),
                width: 170,
            },
            { field: 'shotStatus', headerName: t('studio.studioShotReport.shotStatus'), width: 150 },
            {
                field: 'processedByUser',
                headerName: t('studio.studioShotReport.processedByUser'),
                width: 180,
            },
        ],
        [t]
    );

    const getRowClassName = (params: any) => {
        const createdAt = params.row.createdAt;
        if (!createdAt) return '';

        const createdDate = new Date(createdAt);
        const today = new Date();
        const diffTime = today.getTime() - createdDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays > 3) {
            return 'overdue-row';
        }
        return '';
    };

    const breadcrumbList = [
        { label: t('general.home'), path: '/home' },
        { label: t('studio.title'), path: '/studio' },
        { label: t('studio.studioShotReport.title') },
    ];

    return (
        <SCUIContent>
            {loading && <SCUILoadingLinear />}
            <SCUIBreadCrumb items={breadcrumbList} />

            <Box sx={{ p: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '14px',
                        border: '1px solid #EDEFF2',
                        backgroundColor: '#FAFAFA',
                        p: { xs: 2, sm: 2.5 },
                    }}
                >
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', mb: 2 }}>
                        {t('studio.studioShotReport.title')}
                    </Typography>

                    <FormControl sx={{ mb: 2 }}>
                        <RadioGroup
                            row
                            value={searchMode}
                            onChange={(e) => handleSearchModeChange(e.target.value as SearchMode)}
                        >
                            <FormControlLabel
                                value="input"
                                control={<Radio />}
                                label={t('studio.studioShotReport.searchByProduct')}
                            />
                            <FormControlLabel
                                value="date"
                                control={<Radio />}
                                label={t('studio.studioShotReport.searchByAcceptanceDate')}
                            />
                            <FormControlLabel
                                value="unaccepted"
                                control={<Radio />}
                                label={t('studio.studioShotReport.searchByUnaccepted')}
                            />
                        </RadioGroup>
                    </FormControl>

                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                    >
                        {searchMode === 'input' ? (
                            <>
                                <FormControl sx={{ minWidth: 220 }}>
                                    <Select
                                        value={inputType}
                                        onChange={(e) => setInputType(e.target.value)}
                                        sx={{ height: CONTROL_HEIGHT }}
                                    >
                                        {selectListOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.text}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder={t('studio.studioShotReport.searchPlaceholder')}
                                    fullWidth
                                    sx={{ maxWidth: 420 }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && isSearchEnabled) {
                                            handleSearch();
                                        }
                                    }}
                                    slotProps={{
                                        input: {
                                            sx: { height: CONTROL_HEIGHT },
                                        },
                                    }}
                                />
                            </>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SCUIDateRangerV2
                                    fromDate={startDate}
                                    toDate={endDate}
                                    setDateRange={handleChangeDate}
                                    dateFormat="YYYY-MM-DD"
                                    size="small"
                                    containerStyling={{
                                        width: '280px',
                                        '& .MuiFormControl-root': { margin: 0 },
                                        '& .MuiFormLabel-root': { display: 'none' },
                                        '& .MuiOutlinedInput-root': {
                                            height: `${CONTROL_HEIGHT}px`,
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '12px 14px',
                                            boxSizing: 'border-box',
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<SearchOutlinedIcon />}
                            disabled={!isSearchEnabled}
                            onClick={handleSearch}
                            sx={{
                                height: CONTROL_HEIGHT,
                                minHeight: CONTROL_HEIGHT,
                                maxHeight: CONTROL_HEIGHT,
                                minWidth: 120,
                                borderRadius: '10px',
                                textTransform: 'none',
                                alignSelf: { xs: 'stretch', lg: 'center' },
                            }}
                        >
                            {t('general.search')}
                        </Button>
                    </Stack>
                </Paper>

                {hasSearched && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: '14px',
                            border: '1px solid #EDEFF2',
                            backgroundColor: '#FAFAFA',
                            p: { xs: 2, sm: 2.5 },
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#EFF6FF',
                                        color: '#2563EB',
                                        border: '1px solid #BFDBFE',
                                    }}
                                >
                                    <TableRowsOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
                                    {t('studio.studioShotReport.gridTitle')}
                                </Typography>
                            </Stack>

                            <SCUIDownloadButton
                                position="right"
                                onclick={handleExcelExport}
                                excelData={{
                                    excelDownloadData: [],
                                    excelDownloadFolderName: 'StudioShotReport',
                                    excelDownloadButtonText: t('general.downloadExcel'),
                                }}
                            />
                        </Stack>

                        {rows.length === 0 && !loading ? (
                            <SCUINoRecordComponent text={t('general.noDataFound')} />
                        ) : (
                            <SCUICustomDataGrid
                                rows={rows}
                                columns={columns}
                                pageSize={pageSize}
                                onPageSizeChange={setPageSize}
                                page={currentPage}
                                onPageChange={setCurrentPage}
                                rowCount={totalCount}
                                getRowId={(row: StudioShotReportRow) => row.shotHistoryId ? `sh-${row.shotHistoryId}` : `p-${row.productId}`}
                                paginationModeChange
                                loading={loading}
                                getRowClassName={getRowClassName}
                                sx={{
                                    '& .overdue-row': {
                                        backgroundColor: '#fee2e2 !important',
                                        color: '#991b1b !important',
                                        '&:hover': {
                                            backgroundColor: '#fca5a5 !important',
                                        },
                                    },
                                }}
                            />
                        )}
                    </Paper>
                )}
            </Box>
        </SCUIContent>
    );
};
